/**
 * The country's gateway experiments on the Gateways tab (19/10/2026, step
 * 5), one folded section like the others: closed, how many run and how many
 * concluded; open, one row per experiment — the container, the gateway or
 * lane under test and its share, the metric, day N of M, its status, the
 * last comparison in words, and what was applied. Shown only to roles that
 * see experiments (the same gate as the editor's Experiment step).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Beaker } from 'lucide-react';
import { EXPERIMENT_ACTION_WORDS, EXPERIMENT_METRIC_WORDS, type DropGenExperimentRow } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';

import { useExperiments } from '../../editor/gateways/experiment/use-experiments';
import { ContainerChip } from '../../shared/ContainerChip';
import { useCanSeeExperiments } from '../../shared/experiment/use-can-see-experiments';
import { Collapse } from '../../timeline/section/Collapse';
import { SectionToggle } from '../../timeline/section/SectionToggle';

const GRID = 'grid grid-cols-[minmax(140px,1fr)_minmax(150px,1.2fr)_60px_minmax(90px,0.8fr)_80px_90px_minmax(220px,2fr)] items-center gap-3 px-5';

const TONE: Record<string, 'info' | 'warning' | 'success' | 'secondary'> = { running: 'info', done: 'warning', applied: 'success', stopped: 'secondary' };
const statusWord = (s: string) => s.toLowerCase().replace(/_/g, ' ');

const applied = (e: DropGenExperimentRow): string | null => {
  if (!e.appliedAction) return null;
  const words = EXPERIMENT_ACTION_WORDS[e.appliedAction];
  return words ? words(e.laneLabel ?? e.routeName ?? `gateway ${e.routeId}`, e.sharePct) : e.appliedAction;
};

const summary = (rows: DropGenExperimentRow[]): string => {
  const by = (s: string) => rows.filter((e) => e.status.toLowerCase() === s).length;
  return [by('running') > 0 ? `${by('running')} running` : null, by('done') > 0 ? `${by('done')} concluded, to apply` : null, by('applied') > 0 ? `${by('applied')} applied` : null]
    .filter(Boolean)
    .join(' · ') || `${rows.length} past`;
};

const Row: FC<{ e: DropGenExperimentRow; color: string }> = ({ e, color }) => (
  <div className={`${GRID} border-t border-border/30 py-1.5 text-xs`} data-testid="experiment-row">
    <span><ContainerChip name={e.containerName} color={color} className="!px-2 !text-[10.5px]" /></span>
    <span className="truncate font-medium" title={e.laneLabel ?? e.routeName ?? undefined}>{e.laneLabel ?? e.routeName ?? `Gateway ${e.routeId}`}</span>
    <span className="text-right font-mono tabular-nums">{e.sharePct}%</span>
    <span className="truncate text-muted-foreground">{EXPERIMENT_METRIC_WORDS[e.metric] ?? e.metric}</span>
    <span className="font-mono text-[11px] tabular-nums text-muted-foreground">day {e.dayOf} of {e.days}</span>
    <span><Badge tone={TONE[e.status.toLowerCase()] ?? 'secondary'}>{statusWord(e.status)}</Badge></span>
    <span className="truncate text-muted-foreground" title={[e.result?.words, applied(e)].filter(Boolean).join(' — ')}>
      {e.result?.words ?? 'no comparison yet'}
      {applied(e) ? ` — applied: ${applied(e)}` : ''}
    </span>
  </div>
);

export const ExperimentsSection: FC<{ countryKey: string; colorOf: (containerId: string) => string; scopeIds?: ReadonlySet<string> | null }> = ({ countryKey, colorOf, scopeIds = null }) => {
  const canSee = useCanSeeExperiments();
  const [open, setOpen] = useState(false);
  const experiments = useExperiments(countryKey, canSee);
  const rows = (experiments.data ?? []).filter((e) => !scopeIds || scopeIds.has(e.containerId));
  if (!canSee || rows.length === 0) return null;
  return (
    <div className="border-t border-border/40" data-testid="experiments-section">
      <SectionToggle icon={Beaker} title="Experiments" badge={rows.length} summary={summary(rows)} open={open} onToggle={() => setOpen((v) => !v)} />
      <Collapse open={open}>
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[900px]">
            <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <span>Container</span><span>Gateway under test</span><span className="text-right">Share</span><span>Metric</span><span>Day</span><span>Status</span><span>Result</span>
            </div>
            {rows.map((e) => <Row key={e.id} e={e} color={colorOf(e.containerId)} />)}
          </div>
        </div>
        <p className="px-5 pb-3 text-[11px] text-muted-foreground">An experiment gives one gateway a share of a container's drops and compares it with the rest on the metric; concluded ones are applied from the container editor › Gateways › Experiment.</p>
      </Collapse>
    </div>
  );
};
